/**
 * Accès au module « Technique & Maintenance ».
 *
 * Modèle d'accès à TROIS étages (décision 1 de l'ADR
 * `docs/decisions/2026-08-07-module-technique-maintenance.md`) :
 *
 *   0. `zoo.featureFlags.technicalModule` — flag BETA par parc. Le module est en
 *      rollout progressif : tant que le flag n'est pas posé, ni le menu ni la vue ne
 *      s'ouvrent, quel que soit le droit de l'utilisateur (y compris superAdmin).
 *   1. `rights.technical` — droit GLOBAL. Ouvre le module (menu + vue). Un service
 *      technique est transverse au parc : le rôle secteur seul serait trop étroit.
 *   2. `sectors.technicalUserIds` — rôle SECTEUR. Pilote le routage des notifications
 *      (cf. `roleToFieldMap` dans functions/notes/sendNoteNotifications.js) et sert de
 *      périmètre par défaut dans les écrans, pour les gros parcs à équipes par zone.
 *
 * ⚠️ POINT DE VÉRITÉ UNIQUE : toute décision d'accès au module DOIT passer par
 * `canAccessTechnicalModule`. Ne jamais réintroduire de test `rights.technical` inline
 * dans une vue — c'est exactement la dérive que `noteVisibility.js` a dû rattraper côté véto.
 */

import { isZooFeatureEnabled } from "../zooFeatureFlags";

/** Rôles autorisés à accéder au module sans le droit technique (accès technique/support). */
const TECHNICAL_MODULE_BYPASS_ROLES = ["superAdmin"];

/**
 * Le module Technique est-il activé sur ce parc ?
 * Flag beta `zoo/{zooId}.featureFlags.technicalModule`, fail-closed : un parc sans le
 * champ (cas de tous les parcs existants) se lit comme désactivé.
 *
 * Le flag borne le module AVANT le droit utilisateur, superAdmin compris : un module en
 * beta ne doit pas apparaître chez un parc qui ne l'a pas demandé, même en support.
 *
 * ⚠️ Les flags vivent à DEUX profondeurs selon l'appelant (objet `zooOptions` à plat côté
 * MenuConfig, tranche Redux `state.zoo` côté vues connectées) : la résolution du chemin est
 * déléguée à `isZooFeatureEnabled`, point de vérité unique de la lecture des flags.
 *
 * @param {{ featureFlags?: object, zooOptions?: { featureFlags?: object } }} zoo
 */
export function isTechnicalModuleEnabled(zoo = {}) {
  return isZooFeatureEnabled(zoo, "technicalModule");
}

/**
 * L'utilisateur peut-il accéder au module Technique (bloc latéral + TechnicalDashboardView) ?
 * → flag beta du parc ET (droit technique `rights.technical` ou superAdmin).
 *
 * Périmètre volontairement calqué sur `canAccessVetModule` : un admin SANS droit technique
 * n'a PAS accès au module. Les comptes existants n'ont pas le champ → `undefined` se lit
 * comme `false`, l'activation est un geste admin explicite.
 *
 * @param {{ userRoleValue?: string, userRights?: { technical?: boolean } }} viewer
 * @param {object} zoo options du parc courant (cf. isTechnicalModuleEnabled pour les
 *   deux formes acceptées). Omettre l'argument ferme l'accès : on ne devine pas un flag beta.
 */
export function canAccessTechnicalModule(viewer = {}, zoo = {}) {
  if (!isTechnicalModuleEnabled(zoo)) return false;
  const { userRoleValue, userRights } = viewer;
  if (TECHNICAL_MODULE_BYPASS_ROLES.includes(userRoleValue)) return true;
  return userRights?.technical === true;
}

/**
 * Cet utilisateur fait-il partie de l'équipe technique de ce secteur ?
 * Sert au périmètre par défaut des écrans, PAS à l'accès au module.
 *
 * @param {string} userId
 * @param {{ technicalUserIds?: string[] }} sectorData
 */
export function isTechnicalOfSector(userId, sectorData) {
  if (!userId || !sectorData) return false;
  const ids = sectorData.technicalUserIds;
  return Array.isArray(ids) && ids.includes(userId);
}

/**
 * Les secteurs dont l'utilisateur est référent technique.
 * Liste VIDE = pas de rattachement → les écrans doivent alors montrer TOUT le parc
 * (un technicien transverse n'est référent d'aucun secteur en particulier), et surtout
 * pas un écran vide.
 *
 * @param {string} userId
 * @param {Array<{ sectorId?: string, id?: string, technicalUserIds?: string[] }>} sectors
 * @returns {string[]}
 */
export function technicalSectorIdsForUser(userId, sectors = []) {
  if (!userId || !Array.isArray(sectors)) return [];
  return sectors
    .filter((s) => isTechnicalOfSector(userId, s))
    .map((s) => s.sectorId || s.id)
    .filter(Boolean);
}

/**
 * Normalise un `userData` (formats variés dans l'app) vers la forme attendue par
 * `canAccessTechnicalModule`. Même normalisation que `viewerFromUserData` de
 * noteVisibility.js — dupliquée volontairement pour que ce module reste autonome
 * (le module technique ne dépend pas du module notes).
 */
export function viewerFromUserData(userData = {}) {
  return {
    userRoleValue: userData?.userRight?.value || userData?.userRight || userData?.role,
    userRights: userData?.rights || userData?.userRights,
  };
}
